import axios from "axios";
import { authClientWeb } from "react-redux-django-auth/web";

export interface CompanyInfoData {
  id?: number;
  company_name?: string;
  company_address?: string;
  telephone?: string;
  telephone_2?: string;
  telephone_3?: string;
  email?: string;
  email_2?: string;
  email_3?: string;
  about_company?: string;
  ceo_statment?: string;
  return_policy?: string;
  term_and_conditions?: string;
  privacy_policy?: string;
  career_benefits?: string;
  logo?: string;
  get_logo?: string;
  logo_url?: string;
  logo_media_id?: number | string | null;
  logo_media?: { id: number | string } | null;
  get_page_header_image?: string;
}

export interface CoreValueItem {
  id: number;
  title: string;
  description: string;
  pic?: string;
  pic_url?: string;
  pic_media_id?: number | string | null;
  pic_media?: { id: number | string } | null;
  image_media_id?: number | string | null;
  image_media?: { id: number | string } | null;
}

export interface SocialUrlData {
  id?: number;
  company?: number;
  facebook_url?: string;
  instagram_url?: string;
  twitter_url?: string;
  linkedin_url?: string;
  github_url?: string;
  youtube_url?: string;
  whatsapp_url?: string;
}

const getClient = () => {
  try {
    const client = authClientWeb();
    if (client) return client;
  } catch {
    // fallback if authClientWeb is not initialized
  }
  const token = localStorage.getItem("access");
  const baseURL = process.env.REACT_APP_API_URL || "";
  return axios.create({
    baseURL,
    headers: {
      ...(token ? { Authorization: `JWT ${token}` } : {}),
    },
  });
};

export const companyService = {
  // --- Company Profile Info ---
  async getCompanyInfo(signal?: AbortSignal): Promise<CompanyInfoData | null> {
    const client = getClient();
    try {
      const response = await client.get("/company-info/", { signal });
      if (response && (response.status === 200 || response.status === 201)) {
        const raw = response.data;
        if (Array.isArray(raw)) return raw[0] || null;
        if (raw?.results && Array.isArray(raw.results)) return raw.results[0] || null;
        return raw;
      }
    } catch (err: any) {
      if (axios.isCancel(err)) throw err;
      // Fallback try without trailing slash or coy/ prefix
      try {
        const fallbackRes = await client.get("/coy/company-info/", { signal });
        if (fallbackRes && (fallbackRes.status === 200 || fallbackRes.status === 201)) {
          const raw = fallbackRes.data;
          if (Array.isArray(raw)) return raw[0] || null;
          if (raw?.results && Array.isArray(raw.results)) return raw.results[0] || null;
          return raw;
        }
      } catch (e) {
        console.warn("Company info could not be loaded:", e);
      }
    }
    return null;
  },

  async saveCompanyInfo(data: FormData | Partial<CompanyInfoData>, id?: number): Promise<CompanyInfoData> {
    const client = getClient();
    const isFormData = data instanceof FormData;
    const config = isFormData
      ? { headers: { "Content-Type": "multipart/form-data" } }
      : { headers: { "Content-Type": "application/json" } };

    const endpoint = id ? `/company-info/${id}/` : "/company-info/";
    const method = id ? client.patch : client.post;

    const response = await method(endpoint, data, config);
    if (response && (response.status === 200 || response.status === 201)) {
      return response.data;
    }
    throw new Error(`Failed to save company info (HTTP ${response?.status || "Unknown"})`);
  },

  // --- Core Values ---
  async getCoreValues(signal?: AbortSignal): Promise<CoreValueItem[]> {
    const client = getClient();
    try {
      const response = await client.get("/core-values/", { signal });
      if (response && (response.status === 200 || response.status === 201)) {
        const data = response.data;
        if (Array.isArray(data)) return data;
        if (data && Array.isArray(data.results)) return data.results;
      }
    } catch (err: any) {
      if (axios.isCancel(err)) throw err;
      try {
        const fallbackRes = await client.get("/coy/core-values/", { signal });
        if (fallbackRes && (fallbackRes.status === 200 || fallbackRes.status === 201)) {
          const data = fallbackRes.data;
          if (Array.isArray(data)) return data;
          if (data && Array.isArray(data.results)) return data.results;
        }
      } catch (e) {
        console.warn("Core values could not be loaded:", e);
      }
    }
    return [];
  },

  async createCoreValue(data: FormData | Partial<CoreValueItem>): Promise<CoreValueItem> {
    const client = getClient();
    const isFormData = data instanceof FormData;
    const config = isFormData
      ? { headers: { "Content-Type": "multipart/form-data" } }
      : { headers: { "Content-Type": "application/json" } };

    const response = await client.post("/core-values/", data, config);
    if (response && (response.status === 200 || response.status === 201)) {
      return response.data;
    }
    throw new Error(`Failed to create core value (HTTP ${response?.status || "Unknown"})`);
  },

  async updateCoreValue(id: number, data: FormData | Partial<CoreValueItem>): Promise<CoreValueItem> {
    const client = getClient();
    const isFormData = data instanceof FormData;
    const config = isFormData
      ? { headers: { "Content-Type": "multipart/form-data" } }
      : { headers: { "Content-Type": "application/json" } };

    const response = await client.patch(`/core-values/${id}/`, data, config);
    if (response && (response.status === 200 || response.status === 201)) {
      return response.data;
    }
    throw new Error(`Failed to update core value #${id} (HTTP ${response?.status || "Unknown"})`);
  },

  async deleteCoreValue(id: number): Promise<void> {
    const client = getClient();
    const response = await client.delete(`/core-values/${id}/`);
    if (response && (response.status === 200 || response.status === 204)) {
      return;
    }
    throw new Error(`Failed to delete core value #${id} (HTTP ${response?.status || "Unknown"})`);
  },

  // --- Social URLs ---
  async getSocialUrls(signal?: AbortSignal): Promise<SocialUrlData | null> {
    const client = getClient();
    try {
      const response = await client.get("/social-urls/", { signal });
      if (response && (response.status === 200 || response.status === 201)) {
        const raw = response.data;
        if (Array.isArray(raw)) return raw[0] || null;
        if (raw?.results && Array.isArray(raw.results)) return raw.results[0] || null;
        return raw;
      }
    } catch (err: any) {
      if (axios.isCancel(err)) throw err;
      try {
        const fallbackRes = await client.get("/coy/social-urls/", { signal });
        if (fallbackRes && (fallbackRes.status === 200 || fallbackRes.status === 201)) {
          const raw = fallbackRes.data;
          if (Array.isArray(raw)) return raw[0] || null;
          if (raw?.results && Array.isArray(raw.results)) return raw.results[0] || null;
          return raw;
        }
      } catch (e) {
        console.warn("Social URLs could not be loaded:", e);
      }
    }
    return null;
  },

  async saveSocialUrls(data: Partial<SocialUrlData>, id?: number): Promise<SocialUrlData> {
    const client = getClient();
    const endpoint = id ? `/social-urls/${id}/` : "/social-urls/";
    const method = id ? client.patch : client.post;

    const response = await method(endpoint, data, {
      headers: { "Content-Type": "application/json" },
    });
    if (response && (response.status === 200 || response.status === 201)) {
      return response.data;
    }
    throw new Error(`Failed to save social URLs (HTTP ${response?.status || "Unknown"})`);
  },
};
